import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, X, Share, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { needsHomeScreen, isSnoozed, snooze, PROMPT_SNOOZE_KEY } from '../lib/pushPrompt';
import { InstallGuide } from './InstallGuide';

// One-time, on-brand ask shown after signup / login for members without push
export const PushPrompt = () => {
  const { currentUser } = useAuth();
  const { permission, supported, isEnabled, enableNotifications, loading, restoring } = useNotifications();
  const [open, setOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(() => new URLSearchParams(window.location.search).get('install') === '1');

  useEffect(() => {
    if (!currentUser || currentUser.reviewerMode || isEnabled || restoring || isSnoozed(PROMPT_SNOOZE_KEY)) return undefined;
    // Already allowed in the browser → token is restored silently; never re-ask
    if (permission === 'denied' || permission === 'granted') return undefined;
    if (!supported && !needsHomeScreen()) return undefined;
    const t = setTimeout(() => setOpen(true), 2500);
    return () => clearTimeout(t);
  }, [currentUser, isEnabled, permission, supported, restoring]);

  const dismiss = () => { snooze(PROMPT_SNOOZE_KEY); setOpen(false); };
  const enable = async () => {
    const ok = await enableNotifications();
    if (ok) setOpen(false); else dismiss();
  };
  const homeScreen = needsHomeScreen();

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 24 }} transition={{ duration: 0.25 }}
          className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:w-[380px] z-[60] bg-[#121215] border border-amber-700/50 rounded-xl shadow-2xl shadow-black/80 p-4" data-testid="push-prompt">
          <button onClick={dismiss} className="absolute top-2 right-2 h-7 w-7 flex items-center justify-center rounded text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800" data-testid="push-prompt-dismiss"><X className="w-4 h-4" /></button>
          <div className="flex gap-3">
            <div className="h-10 w-10 rounded-full bg-amber-600/20 border border-amber-500/40 flex items-center justify-center flex-shrink-0">
              {homeScreen ? <Share className="w-5 h-5 text-amber-400" /> : <Bell className="w-5 h-5 text-amber-400" />}
            </div>
            <div className="min-w-0">
              <p className="font-headline tracking-wider text-amber-500 text-sm">DON'T MISS THE DROP</p>
              {homeScreen ? (
                <p className="text-xs text-zinc-300 mt-1 leading-relaxed" data-testid="push-prompt-ios-hint">
                  On iPhone, alerts work once Deadline is on your Home Screen — it takes three quick taps.
                </p>
              ) : (
                <p className="text-xs text-zinc-300 mt-1 leading-relaxed">Get a heads-up when Daria posts, replies to you, or mentions you in chat. No email — just a tap on your device.</p>
              )}
              <div className="flex gap-2 mt-3">
                {homeScreen ? (
                  <button onClick={() => { setGuideOpen(true); setOpen(false); snooze(PROMPT_SNOOZE_KEY); }} className="px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-500 text-black text-xs font-headline tracking-wider" data-testid="push-prompt-show-guide">SHOW ME HOW</button>
                ) : (
                  <button onClick={enable} disabled={loading} className="px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-500 disabled:opacity-60 text-black text-xs font-headline tracking-wider flex items-center gap-1.5" data-testid="push-prompt-enable">
                    {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}ENABLE
                  </button>
                )}
                <button onClick={dismiss} className="px-3 py-1.5 rounded-lg text-zinc-400 hover:text-zinc-100 text-xs font-typewriter" data-testid="push-prompt-later">Not now</button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
      <InstallGuide open={guideOpen} onClose={() => setGuideOpen(false)} />
    </AnimatePresence>
  );
};

export default PushPrompt;
